/// Port of mobile/lib/game/enemy_data.dart.
///
/// The multi-cactus variants are wider crops of the same sheet, so a triple is
/// literally three single widths of `small.png` drawn as one sprite.

import type { Vec2 } from './engine/vector';

export type CactusType =
  | 'smallSingle'
  | 'smallDouble'
  | 'smallTriple'
  | 'largeSingle'
  | 'largeDouble'
  | 'largeTriple'
  | 'mixed';

const smallW = 17.0;
const smallH = 35.0;
const largeW = 25.0;
const largeH = 50.0;
const pteroW = 46.0;
const pteroH = 40.0;

export interface EnemyData {
  imagePath: string;
  stepTime: number;
  textureSize: Vec2;
  srcSize: Vec2;
  srcPosition: Vec2;
  frameCount: number;
  canFly: boolean;
  cactusType: CactusType | null;
  /** Chrome `ObstacleType.minGap`. */
  typeMinGap: number;
  /** Chrome `ObstacleType.minSpeed` (internal 6–13 units). */
  minSpeed: number;
}

function enemyData(options: {
  imagePath: string;
  stepTime: number;
  textureSize: Vec2;
  srcSize: Vec2;
  srcPosition?: Vec2;
  frameCount?: number;
  canFly: boolean;
  cactusType?: CactusType | null;
  typeMinGap?: number;
  minSpeed?: number;
}): EnemyData {
  return {
    imagePath: options.imagePath,
    stepTime: options.stepTime,
    textureSize: options.textureSize,
    srcSize: options.srcSize,
    srcPosition: options.srcPosition ?? { x: 0, y: 0 },
    frameCount: options.frameCount ?? 1,
    canFly: options.canFly,
    cactusType: options.cactusType ?? null,
    typeMinGap: options.typeMinGap ?? 120,
    minSpeed: options.minSpeed ?? 0,
  };
}

export function getEnemyDataList(isDarkMode: boolean): EnemyData[] {
  const mode = isDarkMode ? 'dark' : 'light';

  return [
    enemyData({
      imagePath: `new/cactus/small/${mode}.png`,
      stepTime: Number.POSITIVE_INFINITY,
      textureSize: { x: smallW, y: smallH },
      srcSize: { x: smallW, y: smallH },
      canFly: false,
      cactusType: 'smallSingle',
    }),
    enemyData({
      imagePath: `new/cactus/small/${mode}.png`,
      stepTime: Number.POSITIVE_INFINITY,
      textureSize: { x: smallW * 2, y: smallH },
      srcSize: { x: smallW * 2, y: smallH },
      canFly: false,
      cactusType: 'smallDouble',
    }),
    enemyData({
      imagePath: `new/cactus/small/${mode}.png`,
      stepTime: Number.POSITIVE_INFINITY,
      textureSize: { x: smallW * 3, y: smallH },
      srcSize: { x: smallW * 3, y: smallH },
      canFly: false,
      cactusType: 'smallTriple',
    }),
    enemyData({
      imagePath: `new/cactus/large/${mode}.png`,
      stepTime: Number.POSITIVE_INFINITY,
      textureSize: { x: largeW, y: largeH },
      srcSize: { x: largeW, y: largeH },
      canFly: false,
      cactusType: 'largeSingle',
    }),
    enemyData({
      imagePath: `new/cactus/large/${mode}.png`,
      stepTime: Number.POSITIVE_INFINITY,
      textureSize: { x: largeW * 2, y: largeH },
      srcSize: { x: largeW * 2, y: largeH },
      canFly: false,
      cactusType: 'largeDouble',
    }),
    enemyData({
      imagePath: `new/cactus/large/${mode}.png`,
      stepTime: Number.POSITIVE_INFINITY,
      textureSize: { x: largeW * 3, y: largeH },
      srcSize: { x: largeW * 3, y: largeH },
      canFly: false,
      cactusType: 'largeTriple',
    }),
    enemyData({
      imagePath: `new/cactus/mixed/${mode}.png`,
      stepTime: Number.POSITIVE_INFINITY,
      textureSize: { x: 75, y: 50 },
      srcSize: { x: 75, y: 50 },
      canFly: false,
      cactusType: 'mixed',
    }),
    enemyData({
      imagePath: `new/pterodactyl/${mode}.png`,
      stepTime: 0.2,
      textureSize: { x: pteroW, y: pteroH },
      srcSize: { x: pteroW, y: pteroH },
      frameCount: 2,
      canFly: true,
      typeMinGap: 150,
      minSpeed: 8.5,
    }),
  ];
}
